import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./PersonalInformation.module.scss";
import FormWrapper from "../../../../common/components/form-wrapper/FormWrapper";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import CommonPhoneInput from "../../../../common/components/phone-input/CommonPhoneInput";
import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";
import RadioButtonField from "../../../../common/components/radio-button-field/RadioButtonField";
import {
	emailVerifiedSelector,
	finishPersonalInformation,
	initiateVerifyPhoneNumber,
	personalInformationSelector,
	phoneNumberVerifiedSelector,
	verifyPhoneNumber,
} from "../../../../stores/slices/customer-registration-slice";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import CommonVerifyButton from "../../../../common/components/verify-button/CommonVerifyButton";
import StatesService from "../../../../httpService/StatesService";
import { toast } from "react-toastify";
import CommonDialog from "../../../../common/components/dialog/CommonDialog";
import VerifyPhoneNumberOtpForm from "./components/VerifyPhoneNumberOtpForm";
import { AppDispatch } from "../../../../stores/store";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import CommonFileUploadInput, { StyleOfFileInput } from "../../../../common/components/file-upload-input/CommonFileUploadInput";
import ElectricDistributionCompanyService from "../../../../httpService/ElectricDistributionService";
import { ElectricDistributionCompany } from "../../../../model/electricDistributionCompany";
import { addInstallationAddress } from "../../../../stores/slices/buy-solar-product-slice";
import { FileFromObjectUrl } from "../../../../model/common/file-from-object-url";
import SignUpFormWrapper from "../../../sign-up/wrapper/SignUpFormWrapper";
import CustomerSignUpFormWrapper from "../../../sign-up/wrapper/CustomerSignUpFormWrapper";
import Input from "../../../../common/components/new/ui/Input";
import { EmailIcon, ImageIcon, MapIcon, MeterIcon, NumberedListIcon, PinIcon, ProfileIcon, QuestionIcon, RouteIcon } from "../../../../assets/icons";
import useWindowDimensions, { CurrentScreen } from "../../../../hooks/use-window-dimensions";
import Select from "../../../../common/components/new/select/Select";
import PhoneNumberInput from "../../../../common/components/new/phone-number/PhoneNumberInput";
import FileUploadInput from "../../../../common/components/new/file-upload/FileUploadInput";
import RadioButtonArea, { RadioButtonOption } from "../../../../common/components/new/radio-button-area/RadioButtonArea";

type Props = {
	stepNumber: number;
	onBack: (index: number) => void;
	onNext: (index: number) => void;
};

const PersonalInformation: React.FC<Props> = memo((props: Props) => {
	const currentScreen: CurrentScreen = useWindowDimensions();
	const personalInformation = useSelector(personalInformationSelector);
	const emailVerified = useSelector(emailVerifiedSelector);
	const phoneVerified = useSelector(phoneNumberVerifiedSelector);

	const [states, setStates] = useState<CommonSelectItem[]>([]);
	const isHomeAddressItems: RadioButtonOption[] = [
		{ key: 0, label: "Yes", value: true },
		{ key: 1, label: "No", value: false },
	];
	const [distributionCompanies, setDistributionCompanies] = useState<CommonSelectItem[]>([]);
	const statesService = useMemo(() => new StatesService(), []);

	const [showPhoneVerification, setShowPhoneVerification] = useState(false);

	const dispatch = useDispatch<AppDispatch>();

    const {
        register,
        handleSubmit,
        watch,
        trigger,
        setValue,
        getValues,
        control,
        formState: { errors, isValid, isValidating },
    } = useForm<{
        firstName: string
        lastName: string
        email: string
        emailVerified: boolean
        phoneNumber: string
        phoneNumberVerified: boolean
        state?: CommonSelectItem
        city?: string
        address?: string
        meterNumber?: string
	    electricityDistributionCompany?: CommonSelectItem
        addressProof?: FileFromObjectUrl
        isHouseAddressSameAsInstallationAddress: boolean
        installationAddress?: string
    }>({
        defaultValues: {
            firstName: personalInformation.firstName,
            lastName: personalInformation.lastName,
            email: personalInformation.email,
            phoneNumber: personalInformation.phoneNumber,
            emailVerified: emailVerified,
            state: states.find(x => x.key === personalInformation.state),
            phoneNumberVerified: phoneVerified,
            address: personalInformation.address,
            addressProof: personalInformation.addressProofFile,
            isHouseAddressSameAsInstallationAddress: personalInformation.isHouseAddressSameAsInstallationAddress,
            installationAddress: personalInformation.installationAddress
        },
        reValidateMode: 'onSubmit',
        mode: 'onBlur'
    })

	const openPhoneVerification = () => {
		setShowPhoneVerification(true);
	};

	const closePhoneVerification = () => {
		setShowPhoneVerification(false);
	};

	const togglePhoneVerification = () => {
		setShowPhoneVerification(!showPhoneVerification);
	};

	const initialAddressProof = () => {
		return watch("addressProof") !== undefined ? [watch("addressProof")!.name] : [];
	};

	const onFilesSelection = (file: File[]) => {
		const files: FileFromObjectUrl[] = [];

		file.forEach((f: File) => {
			files.push({
				name: f.name,
				url: URL.createObjectURL(f),
			});
		});

		setValue("addressProof", files[0], { shouldValidate: true, shouldTouch: true, shouldDirty: true });
	};

	const initiatePhoneVerification = async () => {
		try {
			dispatch(showLoader());

			const result = await dispatch(initiateVerifyPhoneNumber({ phoneNumber: watch("phoneNumber") }));

			if (initiateVerifyPhoneNumber.fulfilled.match(result)) {
				toast.success("Phone verification initiated successfully");
				openPhoneVerification();
			} else if (initiateVerifyPhoneNumber.rejected.match(result)) {
				toast.error(result.payload as string);
			}
		} catch (error) {
			toast.error("An error occurred while initiating phone verification");
		} finally {
			dispatch(hideLoader());
		}
	};

	const verifyPhoneOtp = async (otp: string) => {
		const result = await dispatch(
			verifyPhoneNumber({
				otp: otp,
			})
		);

		if (verifyPhoneNumber.fulfilled.match(result)) {
			toast.success("Phone number verified successfully");
			closePhoneVerification();
		} else if (verifyPhoneNumber.rejected.match(result)) {
			toast.error(result.payload as string);
		}
	};

	const fetchStates = useCallback(async () => {
		try {
			let optionsItems: CommonSelectItem[] = [];

			const response = await statesService.getAll();

			response.data.map((item: any) => optionsItems.push(new CommonSelectItem(item.id, item.name)));

			setStates(optionsItems);
		} catch (error) {
			toast.error("There was an error fetching states");
		}
	}, [statesService]);

	const submit = async () => {
		try {
			dispatch(showLoader());

			if (watch("isHouseAddressSameAsInstallationAddress") === false) dispatch(addInstallationAddress(watch("installationAddress")!));
			else dispatch(addInstallationAddress(watch("address")!));

            const result = await dispatch(finishPersonalInformation({
                city: watch('city'),
                email: watch('email'),
                firstName: watch('firstName'),
                lastName: watch('lastName'),
                phoneNumber: watch('phoneNumber'),
                state: watch('state')?.key,
                isHouseAddressSameAsInstallationAddress: watch('isHouseAddressSameAsInstallationAddress'),
                address: watch('address'),
                electricityDistributionCompany: watch('electricityDistributionCompany')?.key,
                meterNumber: watch('meterNumber'),
                addressProofFile: watch('addressProof'),
                installationAddress: watch('installationAddress')
            }))

			if (finishPersonalInformation.fulfilled.match(result)) {
				toast.success("Personal information saved successfully");
				props.onNext(props.stepNumber + 1);
			} else if (finishPersonalInformation.rejected.match(result)) {
				toast.error(result.payload as string);
			}
		} finally {
			dispatch(hideLoader());
		}
	};

	useEffect(() => {
		const loadDistributionCompanies = async () => {
			const service = new ElectricDistributionCompanyService();
			try {
				const response = await service.getAll();
				const companies: CommonSelectItem[] = response.data.map((company: ElectricDistributionCompany) => {
					return new CommonSelectItem(company.name, company.name);
				});
				setDistributionCompanies(companies);
			} catch (error) {
				console.error("Failed to load distribution companies", error);
			}
		};
		loadDistributionCompanies();
	}, []);

	useEffect(() => {
		if (distributionCompanies.length > 0) {
			setValue(
				"electricityDistributionCompany",
				distributionCompanies.find(x => x.key === personalInformation.electricityDistributionCompany) ?? distributionCompanies[0]
			);
		}
	}, [distributionCompanies, setValue, personalInformation.electricityDistributionCompany]);

    useEffect(() => {
		if (distributionCompanies.length > 0) {
			setValue(
				"electricityDistributionCompany",
				distributionCompanies.find(x => x.key === personalInformation.electricityDistributionCompany) ?? distributionCompanies[0]
			);
		}
	}, [distributionCompanies, setValue, personalInformation.electricityDistributionCompany]);



    useEffect(() => {
        setValue('firstName', personalInformation.firstName ?? '')
        setValue('lastName', personalInformation.lastName ?? '')
        setValue('email', personalInformation.email ?? '')
        setValue("meterNumber", personalInformation.meterNumber);
        setValue(
            "electricityDistributionCompany",
            distributionCompanies.find(x => x.key === personalInformation.electricityDistributionCompany) ?? distributionCompanies[0]
        );
        setValue('phoneNumber', personalInformation.phoneNumber ?? '')
        setValue('city', personalInformation.city)
        setValue('state', states.find(x => x.label === personalInformation.state))
        setValue('isHouseAddressSameAsInstallationAddress', personalInformation.isHouseAddressSameAsInstallationAddress ?? true)
        setValue('installationAddress', personalInformation.installationAddress)
    }, [personalInformation, setValue, distributionCompanies, states])

	useEffect(() => {
		fetchStates();
	}, [fetchStates]);

    useEffect(() => {
        setValue('state', states.find(x => x.label === personalInformation.state))
    }, [states, setValue, personalInformation.state])

	useEffect(() => {
		setValue("emailVerified", emailVerified ?? false, {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true,
		});
	}, [emailVerified, setValue]);

    useEffect(() => {
        setValue('phoneNumberVerified', phoneVerified ?? true, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }, [phoneVerified, setValue])

    const registeredInputs =  {
			firstName: register("firstName", { required: "First name is required" }),
			lastName: register("lastName", { required: "Last name is required" }),
			email: register("email", {
				required: "Email is required",
				pattern: {
					value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
					message: "Invalid email address",
				},
			}),
			phoneNumber: register("phoneNumber", { required: "Phone Number is required" }),
			state: register("state", { required: "State is required" }),
			city: register("city", { required: "City / Town is required" }),
			address: register("address", { required: "House address is required" }),
			addressProof: register("addressProof", { required: "Address Proof is required" }),
			isHouseAddressSameAsInstallationAddress: register("isHouseAddressSameAsInstallationAddress"),
			installationAddress: register("installationAddress"),
			meterNumber: register("meterNumber", { required: "Meter Number is required" }),
			electricityDistributionCompany: register("electricityDistributionCompany", { required: "Electricty Distribution Company is required" }),
		};

	useEffect(() => {
		if (watch("isHouseAddressSameAsInstallationAddress"))
			setValue("emailVerified", emailVerified ?? false, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});
	}, [watch, setValue]);

	const onChangeIsHouseAddressSameAsInstallationAddress = (value: boolean) => {
		setValue("isHouseAddressSameAsInstallationAddress", value, {
			shouldValidate: true,
		});

		if (watch("isHouseAddressSameAsInstallationAddress")) {
			registeredInputs.installationAddress = register("installationAddress", { required: false });
		} else {
			registeredInputs.installationAddress = register("installationAddress", { required: "Installation Address is required" });
		}

    } 

    return (
        <>
            <CustomerSignUpFormWrapper
                onBack={() => props.onBack(props.stepNumber - 1)}
                onNext={submit}
                title='Verification Information'
                subtitle='Verify Personal Information'
                labelNextStep='Employment'
                nextButtonDisabled={!isValid}
            >
                <div className={`${styles.container}`}>
                    <Input
                        ref={registeredInputs.firstName.ref}
                        label="First Name"
                        placeholder="e.g Tobi"
                        required
                        error={errors.firstName?.message}
                        inputProps={registeredInputs.firstName}
                    >
                        <ProfileIcon />
                    </Input>
                    <Input
                        ref={registeredInputs.lastName.ref}
                        label="Last Name"
                        placeholder="e.g Gbadamosi"
                        required
                        error={errors.lastName?.message}
                        inputProps={registeredInputs.lastName}
                    >
                        <ProfileIcon />
                    </Input>
                    <Input
                        ref={registeredInputs.email.ref}
                        label="Email address"
                        placeholder="name@sample.com"
                        required
                        error={errors.email?.message}
                        inputProps={registeredInputs.email}
                    >
                        <EmailIcon />
                    </Input>
                    <PhoneNumberInput
                        ref={registeredInputs.phoneNumber.ref}
                        error={errors.phoneNumber?.message}
                        required
                        inputProps={registeredInputs.phoneNumber}
                    />
                    {
                        !currentScreen.isMobile ?
                        <div className={`formFieldInlineContainer ${styles.formInlineRow}`}>
                            <Select
                                label="State"
                                placeholder="Select your State"
                                required
                                error={errors.state?.message}
                                searchable
                                selected={watch('state')}
                                items={states}
                                onChange={value => setValue('state', value)}
                            >
                                <MapIcon />
                            </Select>
                            <Input
                                ref={registeredInputs.city.ref}
                                label="City / Town"
                                placeholder="Select your City / Town"
                                required
                                error={errors.city?.message}
                                inputProps={registeredInputs.city}
                            >
                                <PinIcon />
                            </Input>
                        </div>
                        :
                        <>
                            <Select
                                label="State"
                                placeholder="Select your State"
                                required
                                error={errors.state?.message}
                                searchable
                                selected={watch('state')}
                                items={states}
                                onChange={value => setValue('state', value)}
                            >
                                <MapIcon />
                            </Select>
                            <Input
                                ref={registeredInputs.city.ref}
                                label="City / Town"
                                placeholder="Select your City / Town"
                                required
                                error={errors.city?.message}
                                inputProps={registeredInputs.city}
                            >
                                <PinIcon />
                            </Input>
                        </>
                    }
                    <Input
                        ref={registeredInputs.address.ref}
                        label="House address"
                        placeholder="e.g. parkview estate, Lakeside Avenue"
                        required
                        error={errors.address?.message}
                        inputProps={registeredInputs.address}
                    >
                        <RouteIcon />
                    </Input>
                    <FileUploadInput
                        label='Upload proof of address'
                        maxFileSize={24}
                        accept='image/*'
                        required
                        error={errors.addressProof?.message}
                        styleOfInput={StyleOfFileInput.FormInput}
                        registerInputProps={registeredInputs.addressProof}
                        onFilesSelect={onFilesSelection}
                    >
                        <ImageIcon />
                    </FileUploadInput>
                    <RadioButtonArea
                        options={isHomeAddressItems}
                        initialValue={watch('isHouseAddressSameAsInstallationAddress')}
                        label='Is your house address the same as where you want to install the system?'
                        onChange={onChangeIsHouseAddressSameAsInstallationAddress}
                    >
                        <QuestionIcon/>
                    </RadioButtonArea>
                    {
                        !watch('isHouseAddressSameAsInstallationAddress') &&
                        <Input
                            ref={registeredInputs.installationAddress.ref}
                            label="Installation address"
                            placeholder="e.g. parkview estate, Lakeside Avenue"
                            required
                            error={errors.installationAddress?.message}
                            inputProps={registeredInputs.installationAddress}
                        >
                            <MapIcon />
                        </Input>
                    }
                    <Input
                        ref={registeredInputs.meterNumber.ref}
                        label="Enter meter number"
                        placeholder="1234567890"
                        required
                        error={errors.meterNumber?.message}
                        inputProps={registeredInputs.meterNumber}
                    >
                        <NumberedListIcon />
                    </Input>
                    <Select
                        label="Electricity distribution company"
                        placeholder="Select"
                        required
                        error={errors.electricityDistributionCompany?.message}
                        searchable
                        selected={watch('electricityDistributionCompany')}
                        items={distributionCompanies}
                        onChange={value => setValue('electricityDistributionCompany', value)}
                    >
                        <MeterIcon />
                    </Select>
                </div>
            </CustomerSignUpFormWrapper>
            {
                showPhoneVerification && <CommonDialog>
                    <VerifyPhoneNumberOtpForm
                        onResendOtp={initiatePhoneVerification}
                        onVerifyOtp={verifyPhoneOtp}
                        onCloseDialog={togglePhoneVerification} />
                </CommonDialog>
            }
        </>
    )
})

export default PersonalInformation;
